/**
 * "Widget runtime API" (window.syncanix) — Widget section (ADR-0074).
 * Reconciled from apps/widget main.ts (SyncanixGlobal).
 */
import type { DocPageContent } from '../types';

const EXAMPLE = `// Open the chat programmatically
window.syncanix.open();

// Feed live host-app context into the next turn (bounded JSON, no secrets)
window.syncanix.setContext({ page: 'checkout', cartItems: 3 });

// Provide the signed-in user's token per turn (recommended for SSR apps)
window.syncanix.setTokenProvider(async () => await getAccessToken());`;

export const WIDGET_API_PAGE: DocPageContent = {
  slug: 'widget/runtime-api',
  titleKey: 'docs.widget.api.title',
  descriptionKey: 'docs.widget.api.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.widget.api.lead' },

    { kind: 'heading', level: 2, id: 'methods', textKey: 'docs.widget.api.methods.heading' },
    {
      kind: 'list',
      itemKeys: [
        'docs.widget.api.method.open',
        'docs.widget.api.method.state',
        'docs.widget.api.method.position',
        'docs.widget.api.method.toggle',
        'docs.widget.api.method.unmount',
        'docs.widget.api.method.register',
        'docs.widget.api.method.shadow',
        'docs.widget.api.method.dataSource',
        'docs.widget.api.method.action',
        'docs.widget.api.method.context',
        'docs.widget.api.method.token',
        'docs.widget.api.method.stepup',
        'docs.widget.api.method.headers',
        'docs.widget.api.method.theme',
        'docs.widget.api.method.consent',
        'docs.widget.api.method.sessions',
      ],
    },

    { kind: 'heading', level: 2, id: 'example', textKey: 'docs.widget.api.example.heading' },
    { kind: 'code', language: 'ts', code: EXAMPLE },

    // B19 (operator decision 2026-06-12, closed-by-design): the honest
    // statement of the embedding model — explicit registration, never
    // auto-detection or host-DOM capture (the ADR-0098 security rationale).
    { kind: 'heading', level: 2, id: 'boundary', textKey: 'docs.widget.api.boundary.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.api.boundary.body1' },
    { kind: 'paragraph', textKey: 'docs.widget.api.boundary.body2' },

    { kind: 'heading', level: 2, id: 'idempotent', textKey: 'docs.widget.api.idempotent.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.api.idempotent.body' },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.widget.api.next.config.title',
          textKey: 'docs.widget.api.next.config.text',
          to: { docSlug: 'widget/configuration' },
        },
        {
          titleKey: 'docs.widget.api.next.rendering.title',
          textKey: 'docs.widget.api.next.rendering.text',
          to: { docSlug: 'rendering' },
        },
      ],
    },
  ],
};
